import { useContext } from "react";
import styled from "styled-components";
import Anchor from "~/components/Anchor";
import Block, { BlockContent } from "~/components/Block";
import Divider from "~/components/Divider";
import MediaFull from "~/components/MediaFull";
import { Desktop, Mobile } from "~/components/ShowcaseB";
import { getCommonSliceProps, getRichText, resolveMedia } from "~/external/prismic";
import connectSlice from "~/lib/connectSlice";
import { ThemeContext } from "~/lib/ThemeContext";
import { paragraph } from "~/styles/mixins";
import { smallerThan } from "~/styles/theme";

const FeaturesWrapper = styled.div`
  display: flex;
  flex-direction: row;

  > div:nth-child(1) {
    flex: 1;
    padding-right: ${(p) => p.theme.sizes.hSpacing * 2}rem;
  }

  > div:nth-child(2) {
    flex: 3;
  }

  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    flex-direction: column;
    > div:first-child {
      margin-bottom: ${(p) => p.theme.sizes.vSpacing * 6}rem;
      padding-right: 0;
    }
  }
`;

const FeatureList = styled.div`
  display: grid;
  grid-gap: ${(p) => p.theme.sizes.hSpacing * 2}rem;
  grid-template-columns: 1fr 1fr 1fr 1fr; /* responsive */
  ${(p) => smallerThan("1200px")} {
    grid-template-columns: 1fr 1fr 1fr;
  }
  ${(p) => smallerThan(p.theme.breakpoints.ipadLandscape)} {
    grid-template-columns: 1fr 1fr;
  }
  ${(p) => smallerThan(p.theme.breakpoints.ipadPortrait)} {
    grid-template-columns: 1fr 1fr 1fr;
  }
  ${smallerThan("500px")} {
    grid-template-columns: 1fr 1fr;
  }
`;
const Feature = styled.div<any>`
  background-color: ${(p) => p.backgroundColor} !important;
  p {
    ${paragraph};
    margin-bottom: ${(p) => p.theme.sizes.vSpacing}rem;
  }
`;

const Text = styled.div`
  p {
    ${paragraph};
    margin: ${(p) => p.theme.sizes.vSpacing}rem 0;
  }
`;

const FeatureGrid = styled.div<any>`
  background-color: ${(p) => p.backgroundColor};
  color: ${(p) => p.color};
  display: grid;
  grid-gap: ${(p) => p.theme.sizes.hSpacing * 3}rem;
  ${(p) => p.columnsDesktop === "2 + 1" && `grid-template-columns: 2fr 1fr;`}
  ${(p) => p.columnsDesktop === "1 + 2" && `grid-template-columns: 1fr 2fr;`}
  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    grid-template-columns: 1fr;
  }
`;

type PropType = {
  spacingTop?: number;
  spacingBottom?: number;
  backgroundColor?: string;
  color?: string;
  animateIn?: boolean;

  features: any;
  children: any;
  text: any;
  anchorText?: string;
  anchorPreText?: string;
  anchorId?: string;

  renderStyle: "classic" | "columns";
  columnsDesktop: "2 + 1" | "1 + 2";
  singleMediaColumnMobile: boolean;
  mediaPlacementDesktop: "left" | "right";
  mediaPlacementMobile: "before" | "after";
  media: any[];
};

function ProductFeatures(props: PropType) {
  const currentTheme = useContext(ThemeContext);

  const {
    spacingTop = 0,
    spacingBottom = 6,

    backgroundColor = currentTheme.background,
    color = currentTheme.foreground,
    animateIn = false,

    features = [], // array of feature like ['title', '/img/path', 'text']
    children,
    text,
    anchorText,
    anchorPreText,
    anchorId,

    renderStyle = "classic",
    columnsDesktop,
    singleMediaColumnMobile,
    mediaPlacementDesktop,
    mediaPlacementMobile,
    media = [],
  } = props;

  return (
    <Block
      spacingTop={spacingTop}
      spacingBottom={spacingBottom}
      backgroundColor={backgroundColor}
      color={color}
    >
      <BlockContent>
        {anchorText && (
          <>
            <Anchor id={anchorId} pre={anchorPreText}>
              {anchorText}
            </Anchor>
            <Divider color={color} />
          </>
        )}
        {renderStyle === "classic" && (
          <FeaturesWrapper>
            <div>{children || <Text>{text}</Text>}</div>
            <div>
              <FeatureList style={{ color }}>
                {features.map((feature, i) => (
                  <Feature key={i}>
                    {/* <Anchor></Anchor> */}
                    {/* <Divider /> */}
                    <MediaFull media={feature.media} clickable={false} lazy />
                    <div>
                      <b>{feature.title}</b>
                    </div>
                    {feature.text}
                  </Feature>
                ))}
              </FeatureList>
            </div>
          </FeaturesWrapper>
        )}
        {renderStyle === "columns" && (
          <FeatureGrid columnsDesktop={columnsDesktop} color={color}>
            <div>
              <Desktop>
                {mediaPlacementDesktop === "left" ? (
                  <>
                    {media.map((item, i) => (
                      <MediaFull key={i} media={item} clickable={false} lazy />
                    ))}
                  </>
                ) : (
                  <>
                    {features.map((feature, i) => (
                      <Feature key={i}>
                        <MediaFull media={feature.media} clickable={false} lazy />
                        <div>
                          <b>{feature.title}</b>
                        </div>
                        {feature.text}
                      </Feature>
                    ))}
                  </>
                )}
              </Desktop>
              <Mobile>
                {mediaPlacementMobile === "before" ? (
                  <>
                    {media.map((item, i) => (
                      <MediaFull key={i} media={item} clickable={false} lazy />
                    ))}
                  </>
                ) : (
                  <>
                    {features.map((feature, i) => (
                      <Feature key={i}>
                        <MediaFull media={feature.media} clickable={false} lazy />
                        <div>
                          <b>{feature.title}</b>
                        </div>
                        {feature.text}
                      </Feature>
                    ))}
                  </>
                )}
              </Mobile>
            </div>
            <div>
              <Desktop>
                {mediaPlacementDesktop === "right" ? (
                  <>
                    {media.map((item, i) => (
                      <MediaFull key={i} media={item} clickable={false} lazy />
                    ))}
                  </>
                ) : (
                  <>
                    {features.map((feature, i) => (
                      <Feature key={i}>
                        <MediaFull media={feature.media} clickable={false} lazy />
                        <div>
                          <b>{feature.title}</b>
                        </div>
                        {feature.text}
                      </Feature>
                    ))}
                  </>
                )}
              </Desktop>
              <Mobile>
                {mediaPlacementMobile === "after" ? (
                  <>
                    {media.map((item, i) => (
                      <MediaFull key={i} media={item} clickable={false} lazy />
                    ))}
                  </>
                ) : (
                  <>
                    {features.map((feature, i) => (
                      <Feature key={i}>
                        <MediaFull media={feature.media} clickable={false} lazy />
                        <div>
                          <b>{feature.title}</b>
                        </div>
                        {feature.text}
                      </Feature>
                    ))}
                  </>
                )}
              </Mobile>
            </div>
          </FeatureGrid>
        )}
      </BlockContent>
    </Block>
  );
}

export function mapSliceToProps(slice) {
  const { primary, items } = slice;

  const props = {
    ...getCommonSliceProps(slice),

    text: getRichText(primary.text),
    renderStyle: primary.render_style ?? "classic", // | columns
    columnsDesktop: primary.columns_desktop ?? "2 + 1", // | 1 + 2
    singleMediaColumnMobile: primary.single_media_column_mobile ?? false, // | true
    mediaPlacementDesktop: primary.media_placement_desktop ?? "left", // | right
    mediaPlacementMobile: primary.media_placement_mobile ?? "before", // | after

    features: items
      .filter(
        (item) =>
          (item.image || item.video) && item.title && (item.text?.length ?? 0) > 0
      )
      .map((item) => ({
        title: item.title,
        text: getRichText(item.text),
        // video hae predecense over the image
        media: resolveMedia(item, "1:1", "large"),
        // media: item.video.url
        //   ? getMediaArrayFromVideo(item.video, (item.video_aspect_ratio = '1:1'))
        //   : getMediaArrayFromImage(item.image)
      })),
    media: items
      .filter(
        (item) =>
          (item.image || item.video) && (!item.title || (item.text?.length ?? 0) === 0)
      )
      .map((item) => resolveMedia(item, "1:1", "large")),
  };

  return props;
}

export default connectSlice(mapSliceToProps)(ProductFeatures);
